import ComponentPanel from "./ComponentPanel.js";
import * as DomBuilder from "./dom-builder.js";

/**
 *
 * @version 2026-09-03
 * @author Patrik Harag
 */
export default class ComponentPanelLog extends ComponentPanel {

    /** @type {Controller} */
    #controller;
    /** @type {Logger} */
    #logger;
    /** @type {HTMLElement} */
    #badge;
    /** @type {HTMLElement} */
    #tableBody;
    /** @type {HTMLElement} */
    #emptyHint;
    /** @type {HTMLElement} */
    #tableWrap;
    /** @type {number} */
    #unreadErrors = 0;
    /** @type {number} */
    #lastSeenCount = 0;

    /**
     *
     * @param controller {Controller}
     */
    constructor(controller) {
        super();
        this.#controller = controller;
        this.#logger = controller.getLogger();
        this.#badge = DomBuilder.span('', { class: 'badge text-bg-danger ms-1', style: { display: 'none' } });
        this.#tableBody = DomBuilder.element('tbody');
        this.#emptyHint = DomBuilder.par({ class: 'log-empty' }, 'No data problems recorded.');
        this.#tableWrap = DomBuilder.div({ class: 'table-responsive', style: { display: 'none' } }, [
            DomBuilder.element('table', { class: 'table table-striped' }, [
                this.#tableBody
            ])
        ]);

        this.#logger.addOnUpdated(() => this.#refresh());
    }

    getTabId() {
        return 'finance-tab-log';
    }

    getTabName() {
        return [
            DomBuilder.element("i", { class: "fa fa-exclamation-circle" }),
            DomBuilder.span(" Log"),
            this.#badge
        ];
    }

    createNode() {
        setTimeout(() => this.#bindTabEvents());

        return DomBuilder.div({ class: 'panel-log' }, [
            this.#emptyHint,
            this.#tableWrap
        ]);
    }

    #bindTabEvents() {
        let nav = document.getElementById('finance-tab-log-nav');
        if (nav === null) {
            return;
        }
        nav.addEventListener('shown.bs.tab', () => this.#markRead());
    }

    #refresh() {
        let entries = this.#logger.getEntries();
        this.#tableBody.replaceChildren();

        for (let entry of entries) {
            this.#tableBody.append(DomBuilder.element('tr', null, [
                DomBuilder.element('td', { class: 'log-time' }, this.#formatTime(entry.time)),
                DomBuilder.element('td', { class: 'log-detail' }, [
                    DomBuilder.div({ class: 'log-title' }, entry.title),
                    DomBuilder.element('code', null, entry.message)
                ])
            ]));
        }

        let hasEntries = entries.length > 0;
        this.#emptyHint.style.display = hasEntries ? 'none' : '';
        this.#tableWrap.style.display = hasEntries ? '' : 'none';

        if (!this.#isTabActive()) {
            let newErrors = 0;
            for (let i = this.#lastSeenCount; i < entries.length; i++) {
                if (entries[i].level === 'error') {
                    newErrors++;
                }
            }
            this.#unreadErrors += newErrors;
        }
        this.#lastSeenCount = entries.length;
        this.#updateBadge();
    }

    #markRead() {
        this.#unreadErrors = 0;
        this.#lastSeenCount = this.#logger.getEntries().length;
        this.#updateBadge();
    }

    #updateBadge() {
        if (this.#unreadErrors > 0) {
            this.#badge.textContent = String(this.#unreadErrors);
            this.#badge.style.display = '';
        } else {
            this.#badge.textContent = '';
            this.#badge.style.display = 'none';
        }

        let nav = document.getElementById('finance-tab-log-nav');
        if (nav !== null) {
            if (this.#unreadErrors > 0) {
                nav.classList.add('text-danger');
            } else {
                nav.classList.remove('text-danger');
            }
        }
    }

    #isTabActive() {
        let nav = document.getElementById('finance-tab-log-nav');
        return nav !== null && nav.classList.contains('active');
    }

    /**
     *
     * @param date {Date}
     * @return {string}
     */
    #formatTime(date) {
        let pad = (n, width) => String(n).padStart(width, '0');
        return date.getFullYear()
                + '-' + pad(date.getMonth() + 1, 2)
                + '-' + pad(date.getDate(), 2)
                + ' ' + pad(date.getHours(), 2)
                + ':' + pad(date.getMinutes(), 2)
                + ':' + pad(date.getSeconds(), 2)
                + '.' + pad(date.getMilliseconds(), 3);
    }
}
